import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.4 Tier 2 — Invoices tab controller. Record-invoice modal
// pre-fills unit cost from the PO line so the most-common case (vendor
// billed exactly what we ordered) passes three-way match in one click.
// Admin-only actions: approve, reject, override-hold, mark-paid.
export default class extends Controller {
  static targets = [
    "recordModal", "invoiceNumber", "invoiceDate", "dueDate", "linesBody",
    "invoiceBody", "chevron",
    "rejectModal", "rejectReason",
    "overrideModal", "overrideNote",
    "paidAt"
  ]

  static values = {
    projectId: Number,
    poId: Number,
    canApprove: Boolean,
    lines: Array
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._rejectingInvoiceId = null
    this._overridingInvoiceId = null
  }

  // ── Record invoice ─────────────────────────────────────────────────
  openRecordModal() {
    this._populateLines()
    this._show(this.recordModalTarget)
  }

  hideRecordModal() { this._hide(this.recordModalTarget) }

  async confirmRecord() {
    const rows = this.linesBodyTarget.querySelectorAll("[data-line-id]")
    const lines = []
    rows.forEach(row => {
      const lineId = parseInt(row.dataset.lineId, 10)
      const qty = parseFloat(row.querySelector('[data-field="qty"]').value) || 0
      const unitCost = parseFloat(row.querySelector('[data-field="unit_cost"]').value) || 0
      if (qty > 0) {
        lines.push({
          purchase_order_line_item_id: lineId,
          invoiced_qty: qty,
          unit_cost_recorded: unitCost
        })
      }
    })
    if (lines.length === 0) {
      this._showToast("Enter a quantity on at least one line", "error")
      return
    }

    const body = {
      purchase_order_invoice: {
        vendor_invoice_number: this.invoiceNumberTarget.value.trim() || null,
        invoice_date: this.invoiceDateTarget.value,
        due_date: this.dueDateTarget.value || null,
        line_items: lines
      }
    }
    const resp = await this._ajax("POST", this._invoicesUrl(), body)
    if (!resp.ok) {
      this._showToast(resp.body?.errors?.join(", ") || resp.body?.error || "Could not save invoice", "error")
      return
    }
    this._track("Purchase Order Invoice Recorded", { invoice_id: resp.body.id })
    this._flashAndReload("Invoice recorded")
  }

  toggleInvoice(event) {
    const card = event.currentTarget.closest("[data-invoice-id]")
    const body = card?.querySelector('[data-po-invoices-target="invoiceBody"]')
    const chevron = card?.querySelector('[data-po-invoices-target="chevron"]')
    if (!body) return
    body.classList.toggle("hidden")
    if (chevron) chevron.classList.toggle("rotate-180")
  }

  // ── State transitions ─────────────────────────────────────────────
  async submitInvoice(event) {
    const invoiceId = event.currentTarget.dataset.invoiceId
    const resp = await this._ajax("POST", `${this._invoicesUrl()}/${invoiceId}/submit`)
    if (!resp.ok) { this._showToast(resp.body?.error || "Submit failed", "error"); return }
    const eventName = resp.body?.status === "held"
      ? "Purchase Order Invoice Held"
      : "Purchase Order Invoice Matched"
    this._track(eventName, { invoice_id: parseInt(invoiceId, 10), status: resp.body?.status })
    this._flashAndReload(resp.body?.status === "held" ? "Invoice held — three-way match failed" : "Invoice submitted for approval")
  }

  async approveInvoice(event) {
    const invoiceId = event.currentTarget.dataset.invoiceId
    const resp = await this._ajax("POST", `${this._invoicesUrl()}/${invoiceId}/approve`)
    if (!resp.ok) { this._showToast(resp.body?.error || "Approve failed", "error"); return }
    this._track("Purchase Order Invoice Approved", { invoice_id: parseInt(invoiceId, 10) })
    this._flashAndReload("Invoice approved")
  }

  rejectInvoice(event) {
    this._rejectingInvoiceId = event.currentTarget.dataset.invoiceId
    this._show(this.rejectModalTarget)
  }
  hideRejectModal() {
    this._rejectingInvoiceId = null
    this._hide(this.rejectModalTarget)
  }

  async confirmReject() {
    if (!this._rejectingInvoiceId) return
    const reason = this.rejectReasonTarget.value.trim()
    this._hide(this.rejectModalTarget)
    const resp = await this._ajax("POST", `${this._invoicesUrl()}/${this._rejectingInvoiceId}/reject`, { reason })
    if (!resp.ok) { this._showToast(resp.body?.error || "Reject failed", "error"); return }
    this._track("Purchase Order Invoice Rejected", { invoice_id: parseInt(this._rejectingInvoiceId, 10) })
    this._flashAndReload("Invoice rejected")
  }

  overrideHold(event) {
    this._overridingInvoiceId = event.currentTarget.dataset.invoiceId
    this._show(this.overrideModalTarget)
  }
  hideOverrideModal() {
    this._overridingInvoiceId = null
    this._hide(this.overrideModalTarget)
  }

  async confirmOverride() {
    if (!this._overridingInvoiceId) return
    const note = this.overrideNoteTarget.value.trim()
    this._hide(this.overrideModalTarget)
    const resp = await this._ajax("POST", `${this._invoicesUrl()}/${this._overridingInvoiceId}/override_hold`, { note })
    if (!resp.ok) { this._showToast(resp.body?.error || "Override failed", "error"); return }
    this._track("Purchase Order Invoice Hold Overridden", { invoice_id: parseInt(this._overridingInvoiceId, 10) })
    this._flashAndReload("Hold overridden")
  }

  async markPaid(event) {
    const invoiceId = event.currentTarget.dataset.invoiceId
    const paidAtInput = this.paidAtTargets.find(t => t.dataset.invoiceId === invoiceId)
    const paidAt = paidAtInput?.value || new Date().toISOString().slice(0, 10)
    const resp = await this._ajax("POST", `${this._invoicesUrl()}/${invoiceId}/mark_paid`, { paid_at: paidAt })
    if (!resp.ok) { this._showToast(resp.body?.error || "Mark paid failed", "error"); return }
    this._track("Purchase Order Invoice Paid", { invoice_id: parseInt(invoiceId, 10) })
    this._flashAndReload("Invoice marked paid")
  }

  async deleteInvoice(event) {
    const invoiceId = event.currentTarget.dataset.invoiceId
    const resp = await this._ajax("DELETE", `${this._invoicesUrl()}/${invoiceId}`)
    if (!resp.ok) { this._showToast(resp.body?.error || "Delete failed", "error"); return }
    this._flashAndReload("Invoice discarded")
  }

  stopPropagation(event) { event.stopPropagation() }

  // ── internals ─────────────────────────────────────────────────────
  _populateLines() {
    const body = this.linesBodyTarget
    body.innerHTML = ""

    this.linesValue.forEach(line => {
      const tr = document.createElement("tr")
      tr.dataset.lineId = line.id
      tr.innerHTML = `
        <td class="px-2 py-1 text-gray-500">${line.position}</td>
        <td class="px-2 py-1">${this._escape(line.description || "")}</td>
        <td class="px-2 py-1 text-right tabular-nums text-gray-500">${this._fmt(line.available)}</td>
        <td class="px-2 py-1 text-right">
          <input type="number" step="0.01" min="0" value="0"
                 data-field="qty"
                 class="w-24 text-[13px] text-right border border-gray-300 rounded px-1 py-0.5 tabular-nums">
        </td>
        <td class="px-2 py-1 text-right">
          <input type="number" step="0.01" min="0" value="${this._fmt(line.unit_cost)}"
                 data-field="unit_cost"
                 class="w-28 text-[13px] text-right border border-gray-300 rounded px-1 py-0.5 tabular-nums">
        </td>
      `
      body.appendChild(tr)
    })
  }

  _fmt(n) {
    const v = Number(n) || 0
    return v === Math.trunc(v) ? String(Math.trunc(v)) : v.toFixed(2)
  }

  _escape(str) {
    return String(str ?? "").replace(/[&<>"']/g, ch => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[ch]))
  }

  _invoicesUrl() {
    return `/projects/${this.projectIdValue}/purchase_orders/${this.poIdValue}/invoices`
  }

  _show(el) { el?.classList.remove("hidden") }
  _hide(el) { el?.classList.add("hidden") }

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("purchase_order_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }

  async _ajax(method, url, payload) {
    const options = {
      method,
      headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }
    try {
      const resp = await fetch(url, options)
      const ct = resp.headers.get("Content-Type") || ""
      const body = ct.includes("application/json") ? await resp.json().catch(() => null) : null
      return { ok: resp.ok, status: resp.status, body }
    } catch (e) {
      return { ok: false, status: 0, body: { error: e.message } }
    }
  }

  _track(name, props = {}) {
    try {
      mixpanelTrack(name, { project_id: this.projectIdValue, purchase_order_id: this.poIdValue, ...props })
    } catch { /* ignore */ }
  }
}
